/**
 * Cabinet State Service — Story 21.9
 * Управляет активным кабинетом пользователя.
 * Использует Angular signals + localStorage для персистентности.
 */
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { DemoModeService } from './demo-mode.service';
import { RouteReloadService } from '../routing/route-reload.service';

export interface Cabinet {
  id: string;
  name: string;
  role: string;
}

const STORAGE_KEY = 'active_cabinet_id';
const PRE_DEMO_CABINET_STORAGE_KEY = 'pre_demo_cabinet_id';
export const DEMO_CABINET_ID = 'de000000-0000-4000-8000-000000000001';

@Injectable({ providedIn: 'root' })
export class CabinetStateService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private demoMode = inject(DemoModeService);
  private routeReload = inject(RouteReloadService);

  private _cabinets = signal<Cabinet[]>([]);
  private _activeCabinetId = signal<string>(
    localStorage.getItem(STORAGE_KEY) || ''
  );

  /** Список всех доступных кабинетов */
  readonly cabinets = this._cabinets.asReadonly();

  /** ID активного кабинета */
  readonly activeCabinetId = this._activeCabinetId.asReadonly();

  /** Активный кабинет (объект) */
  readonly activeCabinet = computed(() =>
    this._cabinets().find(c => c.id === this._activeCabinetId()) ?? null
  );

  /** Показывать ли переключатель (2+ кабинета) */
  readonly showSwitcher = computed(() => this._cabinets().length >= 2);

  constructor() {
    effect(() => {
      const isDemo = this.demoMode.isDemo();
      queueMicrotask(() => {
        if (isDemo) {
          this.enableDemoCabinet();
        } else {
          this.disableDemoCabinet();
        }
      });
    });
  }

  /**
   * Загрузить список кабинетов с backend.
   * Восстанавливает выбор из localStorage или использует первый (home).
   * Запрос к /api/v1/user/cabinets отправляется БЕЗ X-Cabinet-ID заголовка
   * (interceptor не добавляет заголовок если список ещё не загружен).
   */
  async loadCabinets(): Promise<void> {
    try {
      const list = await firstValueFrom(
        this.http.get<Cabinet[]>(`${environment.apiUrl}/user/cabinets`)
      );
      this._cabinets.set(list);

      // Восстановить выбор из localStorage, проверяя актуальность
      const saved = localStorage.getItem(STORAGE_KEY);
      if (this.demoMode.isDemo()) {
        this._activeCabinetId.set(DEMO_CABINET_ID);
        localStorage.setItem(STORAGE_KEY, DEMO_CABINET_ID);
        return;
      }

      const validSaved = saved ? list.find(c => c.id === saved) : null;
      if (validSaved) {
        // Сохранённый кабинет актуален — используем его
        this._activeCabinetId.set(saved!);
      } else {
        // Нет сохранённого или он недоступен — использовать первый (home cabinet)
        if (list.length > 0) {
          this._activeCabinetId.set(list[0].id);
          localStorage.setItem(STORAGE_KEY, list[0].id);
        }
      }
    } catch (err) {
      console.error('[CabinetStateService] Failed to load cabinets:', err);
      // При ошибке сбросить невалидный кабинет из localStorage
      localStorage.removeItem(STORAGE_KEY);
      this._activeCabinetId.set('');
    }
  }

  /**
   * Переключить активный кабинет.
   * Сохраняет выбор в localStorage и повторно открывает текущий URL через
   * Angular Router для пересоздания компонентов (перезапрос данных).
   *
   * НЕ использует window.location.reload() — это вызывает повторный Keycloak-редирект
   * из-за того, что при перезагрузке страницы auth-код отсутствует в URL и
   * Keycloak возвращает authenticated=false → redirect → redirect back (2 лишних roundtrip).
   */
  switchCabinet(id: string): void {
    if (id === this._activeCabinetId()) return;
    localStorage.setItem(STORAGE_KEY, id);
    this._activeCabinetId.set(id);

    // onSameUrlNavigation + SameUrlRouteReuseStrategy пересоздают текущий экран
    // без промежуточного перехода на '/', поэтому layout не мигает. Новые запросы
    // получают X-Cabinet-ID из уже обновлённого activeCabinetId сигнала.
    // request() взводит флаг пересоздания ТОЛЬКО для этой навигации (review P1:
    // не пересоздавать компонент на обычный повторный клик по пункту меню).
    const currentUrl = this.router.url;
    this.routeReload.request();
    void this.router.navigateByUrl(currentUrl).finally(() => this.routeReload.clear());
  }

  /**
   * Очистить состояние при logout.
   */
  clear(): void {
    this._cabinets.set([]);
    this._activeCabinetId.set('');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PRE_DEMO_CABINET_STORAGE_KEY);
    this.demoMode.disable();
  }

  private enableDemoCabinet(): void {
    const currentId = this._activeCabinetId();
    if (currentId === DEMO_CABINET_ID) return;

    if (currentId) {
      localStorage.setItem(PRE_DEMO_CABINET_STORAGE_KEY, currentId);
    }

    this.switchCabinet(DEMO_CABINET_ID);
  }

  private disableDemoCabinet(): void {
    if (this._activeCabinetId() !== DEMO_CABINET_ID) {
      return;
    }

    const storedPrevious = localStorage.getItem(PRE_DEMO_CABINET_STORAGE_KEY);
    const fallbackCabinet = this._cabinets().find(c => c.id !== DEMO_CABINET_ID);
    const restoreId =
      storedPrevious && storedPrevious !== DEMO_CABINET_ID
        ? storedPrevious
        : fallbackCabinet?.id ?? '';

    localStorage.removeItem(PRE_DEMO_CABINET_STORAGE_KEY);

    if (restoreId) {
      this.switchCabinet(restoreId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
      this._activeCabinetId.set('');
    }
  }
}
